import {
 Box,
 Button,
 Checkbox,
 CircularProgress,
 Dialog,
 DialogActions,
 DialogContent,
 DialogTitle,
 Divider,
 FormControlLabel,
 LinearProgress,
 Table,
 TableBody,
 TableCell,
 TableRow,
 Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { post } from '../../api/api';
import useWeighing, { WeighTab } from '../../hooks/useWeighing';
import { displayToast } from '../../redux/toast/toastSlice';
import SelectApi from '../common/SelectApi';
import WeighingForm, {
 WeighingFormValues,
 WeighingProductRow,
} from './WeighingForm';

interface SummaryRow {
 label: string;
 value: string;
}

interface WeighingPanelProps {
 scaleId?: number | null;
}

const CHART_MAX = 50000;
const TABS: WeighTab[] = ['pirmas', 'antras', 'kontrolinis'];

const fmtWeight = (w: number | null) => (w == null ? '–' : String(w));
const fmtTime = (iso: string | null) => {
 if (!iso) return '-';
 const d = new Date(iso);
 return Number.isNaN(d.getTime())
  ? '-'
  : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const fmtRemaining = (ms: number | null) => {
 if (ms == null) return '';
 const total = Math.max(0, Math.floor(ms / 1000));
 const h = Math.floor(total / 3600);
 const m = Math.floor((total % 3600) / 60);
 const s = total % 60;
 const mm = String(m).padStart(2, '0');
 const ss = String(s).padStart(2, '0');
 return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
};
const pct = (v: number | null) =>
 v == null ? null : Math.min(100, Math.max(0, (v / CHART_MAX) * 100));

const WeighingPanel = ({ scaleId }: WeighingPanelProps) => {
 const { t } = useTranslation();
 const dispatch = useDispatch();
 const w = useWeighing(scaleId);
 const { control, setValue, watch, register, getValues, reset } = useForm<WeighingFormValues>({
  defaultValues: { ivaz: false, quantity: '' },
 });
 const [products, setProducts] = useState<WeighingProductRow[]>([]);
 const [summaryOpen, setSummaryOpen] = useState(false);
 const [saving, setSaving] = useState(false);
 const [summaryRows, setSummaryRows] = useState<SummaryRow[]>([]);
 const [summaryProducts, setSummaryProducts] = useState<string[]>([]);
 const payloadRef = useRef<Record<string, any> | null>(null);

 // Fields requested from /results/v2 to prefill the form on car select.
 const RESULT_FIELDS = [
  'Car.Id,Car.Number,Trailer1.Id,Trailer1.Number,Trailer2.Id,Trailer2.Number',
  'Sender.Id,Sender.Name,Receiver.Id,Receiver.Name,Carrier.Id,Carrier.Name',
  'Driver.Id,Driver.FullName,Contract.Id,Contract.Number',
  'FromAddress,ToAddress,BrutoDoc,TareDoc,NetoDoc',
  'Add1,Add2,Add3,Add4,Add5,Add6,Add7,Add8',
  'Tare.Weight,Tare.Time,Bruto.Weight,Bruto.Time',
  'ResultProducts.Product.Id,ResultProducts.Product.Name,ResultProducts.Percentage',
 ].join(',');

 // Map a flat /results/v2 row onto react-hook-form fields.
 const fillFromResult = (r: Record<string, any>) => {
  const model = <K extends string>(id: any, labelKey: K, labelVal: any) =>
   id == null
    ? null
    : ({ Id: id, [labelKey]: labelVal } as { Id: number } & Record<K, string>);

  setValue('car', model(r['Car.Id'], 'Number', r['Car.Number']));
  setValue('trailer1', model(r['Trailer1.Id'], 'Number', r['Trailer1.Number']));
  setValue('trailer2', model(r['Trailer2.Id'], 'Number', r['Trailer2.Number']));
  setValue('sender', model(r['Sender.Id'], 'Name', r['Sender.Name']));
  setValue('receiver', model(r['Receiver.Id'], 'Name', r['Receiver.Name']));
  setValue('carrier', model(r['Carrier.Id'], 'Name', r['Carrier.Name']));
  setValue('driver', model(r['Driver.Id'], 'FullName', r['Driver.FullName']));
  setValue('contractDetail', model(r['Contract.Id'], 'Number', r['Contract.Number']));

  setValue('fromAddress', r['FromAddress'] ?? '');
  setValue('toAddress', r['ToAddress'] ?? '');

  const docMap: Record<string, string> = {
   netoDoc: 'NetoDoc',
   brutoDoc: 'BrutoDoc',
   tareDoc: 'TareDoc',
  };
  Object.entries(docMap).forEach(([field, key]) => {
   if (r[key] != null) setValue(field, r[key]);
  });
  for (let i = 1; i <= 8; i += 1) {
   const v = r[`Add${i}`];
   if (v != null) setValue(`add${i}`, v);
  }

  w.setWeights({
   tareWeight: r['Tare.Weight'],
   tareTime: r['Tare.Time'],
   brutoWeight: r['Bruto.Weight'],
   brutoTime: r['Bruto.Time'],
  });

  // Exactly one weight done → first weighing complete, advance to "antras".
  const hasTare = r['Tare.Weight'] != null;
  const hasBruto = r['Bruto.Weight'] != null;
  if (hasTare !== hasBruto) w.setTab('antras');

  const ids: any[] = r['ResultProducts.Product.Id'] || [];
  const names: any[] = r['ResultProducts.Product.Name'] || [];
  const pcts: any[] = r['ResultProducts.Percentage'] || [];
  setProducts(
   ids.map((id, i) => ({
    id: i + 1,
    productId: id,
    name: names[i] ?? '',
    quantity: Number(pcts[i] ?? 0),
   })),
  );
 };

 // On car select: pull the latest open result for that car and prefill.
 const onCarSelect = async (car: { Id?: number; Number?: string } | null) => {
  // Wipe all fields from the previous car; keep the freshly selected car.
  reset({ car: car ?? null, ivaz: false, quantity: '' });
  setProducts([]);
  w.clearWeights();
  if (!car?.Id) return;
  try {
   const { data } = await post('/results/v2', {
    limit: 1,
    fields: RESULT_FIELDS,
    filters: [
     { field: 'Car.Id', operator: 'eq', value: car.Id, logic: 'And' },
     { field: 'State', operator: 'neq', value: 'Filled', logic: 'And' },
     { field: 'State', operator: 'neq', value: 'Approved', logic: 'And' },
     { field: 'State', operator: 'neq', value: 'Aborted' },
    ],
   });
   const row = Array.isArray(data) ? data[0] : null;
   if (row) fillFromResult(row);
  } catch (err) {
   // eslint-disable-next-line no-console
   console.error('[weigh] car prefill failed', err);
  }
 };

 const openSummary = () => {
  const v = getValues();
  const rows: SummaryRow[] = [
   { label: t('weighing.fields.car'), value: v.car?.Number ?? '-' },
   { label: t('weighing.fields.sender'), value: v.sender?.Name ?? '-' },
   { label: t('weighing.fields.carrier'), value: v.carrier?.Name ?? '-' },
   { label: t('weighing.fields.receiver'), value: v.receiver?.Name ?? '-' },
   { label: t('weighing.fields.driver'), value: v.driver?.FullName ?? '-' },
   { label: t('weighing.fields.fromAddress'), value: v.fromAddress || '-' },
   { label: t('weighing.fields.toAddress'), value: v.toAddress || '-' },
   { label: t('weighing.fields.ivaz'), value: v.ivaz ? 'Taip' : 'Ne' },
   {
    label: t('weighing.tare'),
    value: `${fmtWeight(w.tare.weight)}  ${fmtTime(w.tare.time)}`,
   },
   {
    label: t('weighing.bruto'),
    value: `${fmtWeight(w.bruto.weight)}  ${fmtTime(w.bruto.time)}`,
   },
   { label: t('weighing.neto'), value: fmtWeight(w.neto) },
  ];
  setSummaryRows(rows);
  setSummaryProducts(products.map((p) => `${p.name} – ${p.quantity}`));

  // Optional numeric field → number | undefined (empty/invalid dropped).
  const num = (x: unknown) => {
   if (x === '' || x == null) return undefined;
   const n = Number(x);
   return Number.isNaN(n) ? undefined : n;
  };
  const str = (x: unknown) => {
   const s = x == null ? '' : String(x).trim();
   return s === '' ? undefined : s;
  };

  // ResultByOnlineWeighterDTO
  payloadRef.current = {
   Number: str(v.number),
   CarId: v.car?.Id,
   CarNumber: v.car?.Number,
   SenderId: v.sender?.Id,
   SenderName: v.sender?.Name,
   ReceiverId: v.receiver?.Id,
   ReceiverName: v.receiver?.Name,
   CarrierId: v.carrier?.Id,
   CarrierName: v.carrier?.Name,
   DriverId: v.driver?.Id,
   DriverName: v.driver?.FullName,
   Trailer1Id: v.trailer1?.Id,
   Trailer1Number: v.trailer1?.Number,
   Trailer2Id: v.trailer2?.Id,
   Trailer2Number: v.trailer2?.Number,
   ContractDetailId: v.contractDetail?.Id,
   ContractDetailNumber: v.contractDetail?.Number,
   FromAddress: str(v.fromAddress),
   ToAddress: str(v.toAddress),
   NetoDoc: num(v.netoDoc),
   BrutoDoc: num(v.brutoDoc),
   TareDoc: num(v.tareDoc),
   MinNeto: num(v.minNeto),
   MaxNeto: num(v.maxNeto),
   MinBruto: num(v.minBruto),
   MaxBruto: num(v.maxBruto),
   Add1: str(v.add1),
   Add2: str(v.add2),
   Add3: str(v.add3),
   Add4: str(v.add4),
   Add5: str(v.add5),
   Add6: str(v.add6),
   Add7: str(v.add7),
   Add8: str(v.add8),
   TareWeight: w.tare.weight ?? undefined,
   TareTime: w.tare.time ?? undefined,
   BrutoWeight: w.bruto.weight ?? undefined,
   BrutoTime: w.bruto.time ?? undefined,
   CreateIVAZ: !!v.ivaz,
   WeightType: w.tab === 'pirmas' ? 1 : w.tab === 'antras' ? 2 : 3,
   Products: products.map((p) => ({
    ProductId: p.productId,
    ProductName: p.name,
    Quantity: p.quantity,
   })),
  };
  setSummaryOpen(true);
 };

 const handleSave = async (_print: boolean) => {
  if (!payloadRef.current) return;
  setSaving(true);
  try {
   await post('/Special/v1/PartialResultByOnlineWeighter', payloadRef.current);
   dispatch(displayToast({ message: t('weighing.summary.saved'), type: 'success' }));
   setSummaryOpen(false);
  } catch (err: any) {
   dispatch(
    displayToast({
     message: err?.response?.data?.message || err?.message || t('weighing.summary.saveFailed'),
     type: 'error',
    }),
   );
  } finally {
   setSaving(false);
  }
 };

 if (!scaleId) {
  return (
   <Box
    sx={{
     flexGrow: 1,
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
     p: 2,
    }}
   >
    <Typography variant="h5" sx={{ color: (th) => th.palette.kemek.darkGray }}>
     {t('dashboard.onlineWeighing.placeholder')}
    </Typography>
   </Box>
  );
 }

 const busy = w.status === 'loading' || w.status === 'connecting';
 const scaleEmpty = w.live.weight == null || w.live.weight === 0;
 const urgent = w.lock.remainingMs != null && w.lock.remainingMs < 30000;

 const dot = (value: number | null, color: string) => {
  const p = pct(value);
  if (p == null) return null;
  return (
   <Box
    sx={{
     position: 'absolute',
     top: '50%',
     left: `${p}%`,
     transform: 'translate(-50%, -50%)',
     width: 9,
     height: 9,
     borderRadius: '50%',
     backgroundColor: color,
    }}
   />
  );
 };

 return (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flexGrow: 1, gap: 1 }}>
   <Box
    sx={{
     display: 'flex',
     flexDirection: { xs: 'column', md: 'row' },
     gap: 2,
     minHeight: 0,
     flexGrow: 1,
     overflow: 'auto',
    }}
   >
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0, pt: 1 }}>
   {/* Plate number — top-left like original */}
   <SelectApi<WeighingFormValues>
    name="car"
    url="/api/Vehicles/v2/"
    filter={JSON.stringify({ fields: 'Id,Number' })}
    label={t('weighing.fields.car')}
    isEditable
    operator="contains"
    control={control}
    setValue={setValue}
    onSelect={onCarSelect}
   />

   {/* Header: scale name + task + connection + lock */}
   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography sx={{ fontSize: 14, fontWeight: 500, flexGrow: 1 }} noWrap>
     {w.scaleName || `#${scaleId}`}
     {w.live.task ? (
      <Typography component="span" sx={{ ml: 1, fontSize: 12, color: (th) => th.palette.kemek.green }}>
       {w.live.task}
      </Typography>
     ) : null}
     {w.procMsg ? (
      <Typography component="span" sx={{ ml: 1, fontSize: 12, color: (th) => th.palette.kemek.blue }}>
       {w.procMsg}
      </Typography>
     ) : null}
    </Typography>

    {w.lock.locked && (
     <Box
      sx={{
       display: 'flex',
       alignItems: 'center',
       gap: 0.5,
       fontSize: 12,
       color: (th) => th.palette.kemek.red,
       fontWeight: 700,
       animation: urgent ? 'blink 0.55s ease-in-out infinite' : 'none',
       '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
      }}
     >
      <span>{t('weighing.locked')}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
       {fmtRemaining(w.lock.remainingMs)}
      </span>
     </Box>
    )}
    {w.lock.locked && w.lock.holderId && (
     <Typography sx={{ fontSize: 11, color: (th) => th.palette.kemek.gray }} noWrap>
      {t('weighing.lockedBy', { who: w.lock.holderId })}
     </Typography>
    )}
    {w.lock.locked && w.lock.hasToken && (
     <Button size="small" sx={{ minWidth: 'auto', p: '2px 6px' }} onClick={w.extendLock}>
      +
     </Button>
    )}
    <Button
     size="small"
     sx={{ minWidth: 'auto', p: '2px 6px' }}
     onClick={w.toggleLock}
     title={w.lock.locked ? 'unlock' : 'lock'}
    >
     {w.lock.locked ? '🔒' : '🔓'}
    </Button>

    {busy && <CircularProgress size={12} />}
    {(w.status === 'error' || w.status === 'idle') && (
     <Button
      size="small"
      sx={{ minWidth: 'auto', p: '2px 6px' }}
      onClick={w.reconnect}
      title={t('weighing.reconnect')}
     >
      ⟳
     </Button>
    )}
    <Box
     sx={{
      width: 10,
      height: 10,
      borderRadius: '50%',
      flexShrink: 0,
      backgroundColor: (th) =>
       w.status === 'connected'
        ? th.palette.kemek.green
        : w.status === 'error'
        ? th.palette.kemek.red
        : th.palette.kemek.gray,
     }}
    />
   </Box>

   {w.error && (
    <Typography sx={{ fontSize: 12, color: (th) => th.palette.kemek.red }}>
     {w.error}
    </Typography>
   )}

   {/* Tabs */}
   <Box sx={{ display: 'flex', border: (th) => `1px solid ${th.palette.kemek.lightGray}`, borderRadius: 1, overflow: 'hidden' }}>
    {TABS.map((tb) => (
     <Button
      key={tb}
      onClick={() => w.setTab(tb)}
      sx={{
       flex: 1,
       borderRadius: 0,
       fontSize: 13,
       textTransform: 'none',
       color: (th) => (w.tab === tb ? th.palette.primary.main : th.palette.kemek.gray),
       backgroundColor: (th) => (w.tab === tb ? th.palette.kemek.lightBlue : 'transparent'),
       borderBottom: (th) => (w.tab === tb ? `2px solid ${th.palette.primary.main}` : '2px solid transparent'),
      }}
     >
      {t(`weighing.tabs.${tb}`)}
     </Button>
    ))}
   </Box>

   {/* Big weight + stable */}
   <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
    <Typography
     sx={{
      fontSize: 56,
      fontWeight: 400,
      lineHeight: 1,
      color: (th) => (scaleEmpty ? th.palette.kemek.gray : th.palette.kemek.black),
     }}
    >
     {fmtWeight(w.live.weight)}
    </Typography>
    <Box
     sx={{
      width: 16,
      height: 16,
      borderRadius: '50%',
      flexShrink: 0,
      visibility: w.live.weight == null ? 'hidden' : 'visible',
      backgroundColor: (th) => (w.live.stable ? th.palette.kemek.green : th.palette.kemek.red),
      boxShadow: (th) => `0 0 8px ${w.live.stable ? th.palette.kemek.green : th.palette.kemek.red}`,
     }}
    />
   </Box>

   {/* Chart slider: tare(green) bruto(red) live(orange) */}
   <Box sx={{ position: 'relative', height: 4, my: 1.5, borderRadius: 2, backgroundColor: (th) => th.palette.kemek.lightBlue }}>
    {dot(w.tare.weight, '#27ae60')}
    {dot(w.bruto.weight, '#e74c3c')}
    {dot(scaleEmpty ? null : w.live.weight, '#ff9800')}
   </Box>

   {/* Weigh button + progress */}
   <Button
    variant="outlined"
    fullWidth
    disabled={w.weighBusy || scaleEmpty}
    onClick={w.weigh}
   >
    {w.weighBusy ? w.weighLabel || t('weighing.processing') : t('weighing.weigh')}
   </Button>
   {w.weighBusy && <LinearProgress />}

   {/* tare / bruto / neto */}
   <Box
    sx={{
     display: 'grid',
     gridTemplateColumns: 'repeat(3, 1fr)',
     gap: 1,
     p: 1,
     borderRadius: 1,
     backgroundColor: (th) => th.palette.kemek.lightestGray,
    }}
   >
    {[
     { label: t('weighing.tare'), v: w.tare.weight, time: w.tare.time },
     { label: t('weighing.bruto'), v: w.bruto.weight, time: w.bruto.time },
     { label: t('weighing.neto'), v: w.neto, time: null },
    ].map((c) => (
     <Box key={c.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography sx={{ fontSize: 12, color: (th) => th.palette.kemek.gray }}>{c.label}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{fmtWeight(c.v)}</Typography>
      {c.time !== null && (
       <Typography sx={{ fontSize: 11, color: (th) => th.palette.kemek.gray }}>{fmtTime(c.time)}</Typography>
      )}
     </Box>
    ))}
   </Box>

   {/* zero / tare / clear */}
   <Box sx={{ display: 'flex', border: (th) => `1px solid ${th.palette.kemek.lightGray}`, borderRadius: 1, overflow: 'hidden' }}>
    <Button sx={{ flex: 1, borderRadius: 0, fontSize: 12, color: (th) => th.palette.kemek.gray }} onClick={w.sendZero}>
     →0←
    </Button>
    <Button sx={{ flex: 1, borderRadius: 0, fontSize: 12, color: (th) => th.palette.kemek.gray }} onClick={w.sendTare}>
     →T←
    </Button>
    <Button sx={{ flex: 1, borderRadius: 0, minWidth: 'auto', fontSize: 16, color: (th) => th.palette.kemek.gray }} onClick={w.clearWeights}>
     ↻
    </Button>
   </Box>

   <FormControlLabel
    control={
     <Controller
      name="ivaz"
      control={control}
      render={({ field }) => (
       <Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
      )}
     />
    }
    label={t('weighing.fields.ivaz')}
   />

   {w.error && (
    <Typography sx={{ fontSize: 11, color: (th) => th.palette.kemek.red }} noWrap>
     {w.error}
    </Typography>
   )}
    </Box>

    <Box sx={{ flex: 1, minWidth: 0, pt: 1 }}>
     <WeighingForm
      control={control}
      setValue={setValue}
      watch={watch}
      register={register}
      products={products}
      setProducts={setProducts}
     />
    </Box>
   </Box>

   {/* Save bar */}
   <Button variant="contained" fullWidth onClick={openSummary}>
    {t('weighing.summary.save')}
   </Button>

   {/* Result summary before saving */}
   <Dialog open={summaryOpen} onClose={() => setSummaryOpen(false)} fullWidth maxWidth="sm">
    <DialogTitle>{t('weighing.summary.title')}</DialogTitle>
    <DialogContent>
     <Table size="small">
      <TableBody>
       {summaryRows.map((r) => (
        <TableRow key={r.label}>
         <TableCell sx={{ width: '40%', color: (th) => th.palette.kemek.gray }}>
          {r.label}
         </TableCell>
         <TableCell sx={{ fontWeight: 600 }}>{r.value}</TableCell>
        </TableRow>
       ))}
      </TableBody>
     </Table>
     {summaryProducts.length > 0 && (
      <>
       <Divider sx={{ my: 1.5 }} />
       <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
        {t('weighing.products.title')}
       </Typography>
       {summaryProducts.map((p) => (
        <Typography key={p} sx={{ fontSize: 13 }}>
         {p}
        </Typography>
       ))}
      </>
     )}
    </DialogContent>
    <DialogActions>
     <Button color="secondary" disabled={saving} onClick={() => setSummaryOpen(false)}>
      {t('weighing.summary.cancel')}
     </Button>
     <Button disabled={saving} onClick={() => handleSave(false)}>
      {t('weighing.summary.confirm')}
     </Button>
     <Button variant="contained" disabled={saving} onClick={() => handleSave(true)}>
      {saving ? <CircularProgress size={18} /> : t('weighing.summary.confirmPrint')}
     </Button>
    </DialogActions>
   </Dialog>
  </Box>
 );
};

export default WeighingPanel;
